<template>
  <div
    v-if="context"
    class="flex w-full items-center space-x-8 border-t border-gray-100 bg-white p-4"
    data-cy="sticky-footer"
  >
    <button
      v-if="context.backButtonAction"
      class="flex h-full w-14 items-center justify-center rounded-sm border border-border-primary py-4"
      data-cy="back-button"
      @click="context.backButtonAction"
    >
      <icon-less-than />
    </button>
    <input-button
      class="w-full"
      :variant="context.primaryButtonVariant"
      data-cy="primary-button"
      @click="context.primaryButtonAction"
    >
      {{ context.text }}
    </input-button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import useApplicationsState from "@/composables/state/applicationsState";
import { useCheckoutState } from "@/composables/state/checkoutState";
import IconLessThan from "@/components/icons/IconLessThan.vue";
import InputButton from "@/components/input/InputButton.vue";
import type { InputButtonVariant } from "@/types/CommonTypes";

type Context = {
  text: string;
  primaryButtonVariant: InputButtonVariant;
  primaryButtonAction: () => unknown;
  backButtonAction?: () => unknown;
};

const { t } = useI18n();
const { selectedApplication, currentStageButtonConfig, openCurrentStageButtonUrl } =
  useApplicationsState();
const {
  isActive: checkoutIsActive,
  navigateSteps: navigateCheckoutSteps,
  firstStepIsSelected,
} = useCheckoutState();

const fundingAdvisorPhoneNumber = computed(
  () => selectedApplication.value?.funding_advisor?.telephone
);

const context = computed(() => {
  if (checkoutIsActive.value) {
    return contexts.value["checkout"];
  }

  if (currentStageButtonConfig.value.showButton) {
    return contexts.value["application-button"];
  }

  if (fundingAdvisorPhoneNumber.value) {
    return contexts.value["contact-funding-advisor"];
  }

  return null;
});

const contactFundingAdvisor = () => {
  if (!fundingAdvisorPhoneNumber.value) {
    return;
  }

  const phoneCallLink = document.createElement("a");
  phoneCallLink.setAttribute("href", `tel:${fundingAdvisorPhoneNumber.value}`);
  phoneCallLink.click();
};

const contexts = computed<Record<string, Context>>(() => {
  return {
    "application-button": {
      text: currentStageButtonConfig.value.text,
      primaryButtonVariant: "primary",
      primaryButtonAction: openCurrentStageButtonUrl,
    },
    checkout: {
      text: t("checkout.nextStep"),
      primaryButtonVariant: "secondary",
      primaryButtonAction: () => {
        navigateCheckoutSteps("next");
      },
      backButtonAction: !firstStepIsSelected.value
        ? () => navigateCheckoutSteps("previous")
        : undefined,
    },
    "contact-funding-advisor": {
      text: t("footerControls.contactFundingAdvisor"),
      primaryButtonVariant: "primary",
      primaryButtonAction: contactFundingAdvisor,
    },
  };
});

defineExpose({
  context,
});
</script>
